// src/pages/KepithorGames.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllGamesSync } from "../data/allGames";
import AllGameCard from "../components/AllGameCard";

export default function KepithorGames() {
  const navigate = useNavigate();
  const allGames = getAllGamesSync();

  // Only Kepithor games
  const kepithorList = useMemo(
    () => allGames.filter((g) => (g.source || "").toLowerCase() === "kepithor"),
    [allGames]
  );

  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");
  const [tag, setTag] = useState("All");
  const [web3Only, setWeb3Only] = useState(false); // ⭐ Web3 toggle
  const [web2Only, setWeb2Only] = useState(false);

  // Genres from Kepithor games
  const genres = useMemo(() => {
    const set = new Set(["All"]);
    kepithorList.forEach((g) => g.genre && set.add(g.genre));
    return [...set];
  }, [kepithorList]);

  // Tags from Kepithor games
  const tags = useMemo(() => {
    const set = new Set(["All"]);
    kepithorList.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return [...set];
  }, [kepithorList]);

  // Filtered results
  const results = useMemo(() => {
    let list = [...kepithorList];

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => {
        const haystack = [
          g.title,
          g.description,
          g.genre,
          ...(g.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (genre !== "All") {
      list = list.filter((g) => g.genre === genre);
    }

    if (tag !== "All") {
      list = list.filter((g) => (g.tags || []).includes(tag));
    }

    if (web3Only) {
      list = list.filter((g) => g.isWeb3);
    }
    if (web2Only) {
      list = list.filter((g) => !g.isWeb3);
    }

    return list;
  }, [kepithorList, query, genre, tag, web3Only, web2Only]);

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Kepithor Games</h1>
        <p className="text-sm text-slate-300 mt-1">
          Games developed or published by Kepithor Studios.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 shadow-sm shadow-emerald-500/10">

        {/* Search */}
        <input
          placeholder="Search games…"
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 min-w-[240px] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {/* Genre */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          {genres.map((g) => (
            <option key={g} value={g}>
              {g === "All" ? "All genres" : g}
            </option>
          ))}
        </select>

        {/* Tag */}
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {tags.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "All tags" : t}
            </option>
          ))}
        </select>

        {/* ⭐ Web3 pill toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb3Only(!web3Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web3Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web3Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web3 only</span>
        </div>




        {/* ⭐ Web2 pill toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeb2Only(!web2Only)}
            className={`
              relative inline-flex h-6 w-12 items-center rounded-full
              transition
              ${web2Only ? "bg-emerald-500" : "bg-slate-700"}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white shadow
                transition
                ${web2Only ? "translate-x-6" : "translate-x-1"}
              `}
            />
          </button>
          <span className="text-sm text-slate-200">Web2 only</span>
        </div>




      </div>

      {/* Game list */}
      {results.length === 0 ? (
        <p className="text-sm text-slate-400">No games match your filters.</p>
      ) : (
        <div className="space-y-3">
          {results.map((game) => (
            <AllGameCard
              key={game.id}
              game={game}
              onOpen={() => navigate(`/game/${game.id}`)}
              onPlay={
                game.webglPath ? () => navigate(`/play/${game.id}`) : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
